// The RAG document-chat panel: streams LLM answers grounded in uploaded guideline PDFs
// via AskDocumentQuestionStreamSpecification (rag/chat.py's ask_question_stream).
// Optionally blends in ML-prediction or monitoring context (fetched via
// DataTalkContextSpecification) so the LLM can relate guideline content to this specific
// patient. Cited source chunks are shown as clickable chips that open SourceDocumentModal
// with the original PDF page highlighted.
import { useEffect, useState } from "react"
import {
    AnalysisMessageEntry,
    AppendAnalysisMessageSpecification,
    AskDocumentQuestionResponsePayload,
    AskDocumentQuestionStreamSpecification,
    ContextOption,
    DataTalkContextSpecification,
    DownloadDocumentSpecification,
    ValueType,
} from "pokal_api"
import SourceDocumentModal from "./SourceDocumentModal"
import type { MessageSource as UtilMessageSource } from "./sourceModalUtils"
import ChatShell from "../shared/ChatShell"
import { useChatStream } from "../shared/useChatStream"
import ConversationSelector from "../shared/ConversationSelector"
import { useAppSelector } from "../../../../redux/store/hooks"
import { selectOpenAIKey } from "../../../../redux/AccountSettings"

type MessageSource = AskDocumentQuestionResponsePayload["sources"][number]

type RelevantDocumentEntry = {
    document_id: string
    document_title: string
    sources: MessageSource[]
    maxSimilarity: number
}

type LeitlinienExtras = { sources: MessageSource[] }

type Props = {
    patientId: string
    pageContext: "monitoring" | "prediction"
    selectedDocumentIds: string[]
    similarityThreshold: number
    topK: number
    predictionContextEnabled?: boolean
    contextSelections?: Partial<Record<ValueType, ContextOption[]>>
}

export default function LeitlinienChatPanel({
    patientId,
    pageContext,
    selectedDocumentIds,
    similarityThreshold,
    topK,
    predictionContextEnabled = false,
    contextSelections,
}: Props) {
    const [openingSourceKey, setOpeningSourceKey] = useState<string | null>(null)
    const [isSourceModalOpen, setIsSourceModalOpen] = useState(false)
    const [isSourceModalLoading, setIsSourceModalLoading] = useState(false)
    const [sourceModalError, setSourceModalError] = useState<string | null>(null)
    const [sourceModalDocumentUrl, setSourceModalDocumentUrl] = useState<string | null>(null)
    const [sourceModalDocumentTitle, setSourceModalDocumentTitle] = useState<string>("Dokument")
    const [sourceModalSources, setSourceModalSources] = useState<UtilMessageSource[]>([])
    const [sourceModalActiveSourceIndex, setSourceModalActiveSourceIndex] = useState<number>(0)

    // bucket by page identity, not the "include prediction data" toggle below - otherwise
    // this page's history collides with the Monitoring page's whenever that toggle is off
    const conversationMode: "leitlinienMonitoring" | "leitlinienPrediction" =
        pageContext === "prediction" ? "leitlinienPrediction" : "leitlinienMonitoring"
    const openaiApiKey = useAppSelector(selectOpenAIKey)
    const [conversationId, setConversationId] = useState<string | null>(null)
    const [initialMessages, setInitialMessages] = useState<
        { role: "user" | "assistant"; content: string; extras?: LeitlinienExtras }[]
    >([])

    useEffect(() => {
        return () => {
            if (sourceModalDocumentUrl !== null) {
                window.URL.revokeObjectURL(sourceModalDocumentUrl)
            }
        }
    }, [sourceModalDocumentUrl])

    const chat = useChatStream<LeitlinienExtras>({
        patientId,
        conversationId,
        initialMessages,
        onUserMessage: async content => {
            if (!conversationId) return
            try {
                await AppendAnalysisMessageSpecification.request({
                    conversationId,
                    role: "user",
                    content,
                })
            } catch {}
        },
        // No onAssistantMessage: the backend now persists the assistant's reply via
        // StreamPokalRoute's onStreamComplete, regardless of whether this tab is still
        // connected. Persisting it again here would create a duplicate message.
        startStream: async (message, history, model) => {
            let patientCtx: string | undefined
            if (predictionContextEnabled) {
                try {
                    const { context } = await DataTalkContextSpecification.request({
                        patientId,
                        mode: "prediction",
                    })
                    patientCtx = context
                } catch {}
            } else if (contextSelections && Object.values(contextSelections).some(opts => opts?.length)) {
                try {
                    const { context } = await DataTalkContextSpecification.request({
                        patientId,
                        mode: "monitoring",
                        context_selections: contextSelections,
                    } as Parameters<typeof DataTalkContextSpecification.request>[0])
                    patientCtx = context
                } catch {}
            }

            // conversationId rides along on the request body so the backend can persist
            // the assistant's reply itself once generation finishes - this lets the answer
            // survive a page refresh mid-stream. Not part of the pokal_api request type
            // (avoids a shared-package publish); the backend reads it off req.body directly.
            const body = {
                question: message,
                document_ids: selectedDocumentIds.length > 0 ? selectedDocumentIds : undefined,
                top_k: topK,
                similarity_threshold: similarityThreshold,
                llm_model: model,
                patient_context: patientCtx,
                conversation_history: history.map(m => ({ role: m.role, content: m.content })),
                openai_api_key: openaiApiKey ?? undefined,
                conversationId,
            }
            return (await AskDocumentQuestionStreamSpecification.request(body)) as unknown as ReadableStream
        },
        parseDone: data => ({ sources: (data.sources as MessageSource[] | undefined) ?? [] }),
    })

    const buildRelevantDocuments = (sources: MessageSource[]): RelevantDocumentEntry[] => {
        const byDocument = new Map<string, RelevantDocumentEntry>()
        for (const source of sources) {
            const existing = byDocument.get(source.document_id)
            if (existing === undefined) {
                byDocument.set(source.document_id, {
                    document_id: source.document_id,
                    document_title: source.document_title,
                    sources: [source],
                    maxSimilarity: source.similarity,
                })
                continue
            }
            existing.sources.push(source)
            existing.maxSimilarity = Math.max(existing.maxSimilarity, source.similarity)
        }
        return Array.from(byDocument.values())
            .map(entry => ({
                ...entry,
                sources: [...entry.sources].sort((a, b) => b.similarity - a.similarity),
            }))
            .sort((a, b) => b.maxSimilarity - a.maxSimilarity)
    }

    const closeSourceModal = () => {
        setIsSourceModalOpen(false)
        setIsSourceModalLoading(false)
        setSourceModalError(null)
        setSourceModalDocumentTitle("Dokument")
        setSourceModalSources([])
        setSourceModalActiveSourceIndex(0)
        if (sourceModalDocumentUrl !== null) {
            window.URL.revokeObjectURL(sourceModalDocumentUrl)
            setSourceModalDocumentUrl(null)
        }
    }

    const handleOpenRelevantDocument = async (entry: RelevantDocumentEntry) => {
        const openKey = entry.document_id
        if (openingSourceKey === openKey) return
        setOpeningSourceKey(openKey)
        setIsSourceModalOpen(true)
        setIsSourceModalLoading(true)
        setSourceModalError(null)
        setSourceModalDocumentTitle(entry.document_title)
        setSourceModalSources(entry.sources as unknown as UtilMessageSource[])
        setSourceModalActiveSourceIndex(0)
        try {
            const documentBlob = await DownloadDocumentSpecification.request({
                document_id: entry.document_id,
            })
            if (!(documentBlob instanceof Blob)) throw new Error("Failed to load document blob")
            const blobUrl = window.URL.createObjectURL(documentBlob)
            if (sourceModalDocumentUrl !== null) window.URL.revokeObjectURL(sourceModalDocumentUrl)
            setSourceModalDocumentUrl(blobUrl)
        } catch {
            setSourceModalError("Das Dokument konnte nicht geladen werden.")
        } finally {
            setIsSourceModalLoading(false)
            setOpeningSourceKey(null)
        }
    }

    const renderSourceChips = (extras: LeitlinienExtras) => {
        if (!extras.sources.length) return null
        return (
            <div className="flex flex-wrap gap-1 mt-2">
                {buildRelevantDocuments(extras.sources).map(entry => (
                    <button
                        key={entry.document_id}
                        onClick={() => handleOpenRelevantDocument(entry)}
                        disabled={openingSourceKey === entry.document_id}
                        className="bg-white border border-blue-300 text-blue-600 text-xs px-2 py-0.5 rounded-full hover:bg-blue-50 disabled:opacity-50"
                    >
                        📄 {openingSourceKey === entry.document_id ? "Lade..." : entry.document_title}
                    </button>
                ))}
            </div>
        )
    }

    return (
        <>
            <ConversationSelector
                patientId={patientId}
                mode={conversationMode}
                onConversationChange={(id, messages) => {
                    setInitialMessages(
                        messages.map((m: AnalysisMessageEntry) => ({
                            role: m.role,
                            content: m.content,
                            extras: { sources: [] },
                        }))
                    )
                    setConversationId(id)
                }}
            />
            <ChatShell
                messages={chat.messages}
                streaming={chat.streaming}
                loading={chat.loading}
                input={chat.input}
                onInputChange={chat.setInput}
                onSubmit={chat.handleSubmit}
                availableModels={chat.availableModels}
                selectedModel={chat.selectedModel}
                onModelChange={chat.setSelectedModel}
                emptyHint="Stellen Sie Fragen zu Daten und/oder hochgeladenen klinischen Leitlinien."
                placeholder="Frage zu Daten/Leitlinien stellen…"
                renderMessageExtras={renderSourceChips}
            />
            <SourceDocumentModal
                isOpen={isSourceModalOpen}
                isLoading={isSourceModalLoading}
                error={sourceModalError}
                documentUrl={sourceModalDocumentUrl}
                documentTitle={sourceModalDocumentTitle}
                sources={sourceModalSources}
                activeSourceIndex={sourceModalActiveSourceIndex}
                onActiveSourceIndexChange={setSourceModalActiveSourceIndex}
                onClose={closeSourceModal}
            />
        </>
    )
}
